import { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Html5Qrcode } from "html5-qrcode";
import { Camera, LogIn, LogOut, Utensils, ShieldAlert, Loader2 } from "lucide-react";

const API = '/api';

export default function GateAccess() {
  const { code } = useParams();
  const [status, setStatus] = useState('loading'); // loading | valid | invalid | input
  const [gateType, setGateType] = useState(null);
  const [gateLabel, setGateLabel] = useState('');
  const [inputCode, setInputCode] = useState('');
  const [activeCode, setActiveCode] = useState(() => sessionStorage.getItem('campoAccessPin') || '');
  const [scanning, setScanning] = useState(false);
  const [lastScan, setLastScan] = useState(null);
  const [direzione, setDirezione] = useState('ENTRATA');
  const [tipoPasto, setTipoPasto] = useState('pranzo');
  const scannerRef = useRef(null);
  const fileInputRef = useRef(null);
  
    const [manualQr, setManualQr] = useState('');

  const _gateTypeFromUrl = (urlCode) => {
    const v = (urlCode || '').toLowerCase();
    if (v.startsWith('mensa')) return { type: 'mensa', label: 'Gate Mensa' };
    return { type: 'mobile', label: 'Gate ANC' };
  };

  const validateCode = useCallback(async (c) => {
    const pin = (c || '').trim();
    if (!/^\d{6}$/.test(pin)) { setStatus('invalid'); return; }
    setStatus('loading');
    try {
      const res = await fetch(`${API}/gate/verify-pin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin })
      });
      const data = await res.json();
      if (res.ok && data.valid) {
        const gt = _gateTypeFromUrl(code);
        setGateType(gt.type);
        setGateLabel(gt.label);
        setActiveCode(pin);
        try {
          sessionStorage.setItem('campoAccessGranted', 'true');
          sessionStorage.setItem('campoAccessPin', pin);
        } catch (e) {}
        setStatus('valid');
      } else {
        setStatus('invalid');
      }
    } catch {
      setStatus('invalid');
    }
  }, [code]);

  useEffect(() => {
    const granted = sessionStorage.getItem('campoAccessGranted') === 'true';
    const savedPin = sessionStorage.getItem('campoAccessPin') || '';
    if (granted && /^\d{6}$/.test(savedPin)) {
      const gt = _gateTypeFromUrl(code);
      setGateType(gt.type);
      setGateLabel(gt.label);
      setActiveCode(savedPin);
      setStatus('valid');
    } else {
      setStatus('input');
    }
  }, [code]);

  const handleCodeSubmit = (e) => {
    e.preventDefault();
    validateCode(inputCode.trim());
  };

  const startScanner = async () => {
    // === Camera diagnostics block ===
    const log = (...a) => console.log('[CAMERA]', ...a);
    const warn = (...a) => console.warn('[CAMERA]', ...a);
    const err = (...a) => console.error('[CAMERA]', ...a);

    const mapErr = (e) => {
      const n = e?.name || 'UnknownError';
      const m = e?.message || '';
      if (n === 'NotAllowedError' || n === 'PermissionDeniedError') return 'Accesso alla fotocamera negato. Consenti la fotocamera nelle impostazioni del browser e riprova.';
      if (n === 'NotReadableError' || n === 'TrackStartError') return 'La fotocamera risulta occupata da altra app o tab. Chiudi le altre app e riprova.';
      if (n === 'NotFoundError' || n === 'DevicesNotFoundError') return 'Nessuna fotocamera disponibile su questo dispositivo.';
      if (n === 'OverconstrainedError' || n === 'ConstraintNotSatisfiedError') return 'La configurazione richiesta per la fotocamera non e supportata.';
      if (n === 'SecurityError') return 'Accesso alla fotocamera bloccato dalla sicurezza del browser/sito.';
      if (n === 'TypeError') return 'La fotocamera non puo essere avviata in questo contesto (verifica HTTPS).';
      if (n === 'AbortError') return 'Avvio fotocamera interrotto. Riprova.';
      if (n === 'UnknownError') return 'Fotocamera non disponibile (driver/hardware o app esterna in conflitto). Su mobile chiudi altre app camera; su desktop verifica webcam collegata e permessi OS.';
      return `Errore camera: ${n}${m ? ' - ' + m : ''}`;
    };

    console.group('[CAMERA DIAGNOSTICS]');
    log('URL:', window.location.href);
    log('secureContext:', window.isSecureContext);
    log('mediaDevices:', !!navigator.mediaDevices);
    log('getUserMedia:', !!navigator.mediaDevices?.getUserMedia);
    log('enumerateDevices:', !!navigator.mediaDevices?.enumerateDevices);
    log('userAgent:', navigator.userAgent);

    if (!window.isSecureContext) {
      err('Not a secure context');
      toast.error('Pagina non in secure context: la fotocamera non puo essere usata.');
      console.groupEnd();
      return;
    }
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      err('mediaDevices/getUserMedia non disponibili');
      toast.error('Questo browser non supporta l\'accesso alla fotocamera.');
      console.groupEnd();
      return;
    }

    let permState = 'unsupported';
    try {
      if (navigator.permissions?.query) {
        const r = await navigator.permissions.query({ name: 'camera' });
        permState = r.state;
      }
    } catch (e) { warn('permissions.query failed', e); }
    log('permission state:', permState);

    let videoDevices = [];
    try {
      const all = await navigator.mediaDevices.enumerateDevices();
      videoDevices = all.filter(d => d.kind === 'videoinput');
      log('video devices:', videoDevices);
    } catch (e) { warn('enumerateDevices failed', e); }

    // Probe getUserMedia with progressive constraints to surface the REAL error
    const attempts = [
      { label: 'rear-ideal', c: { video: { facingMode: 'environment' }, audio: false } },
      { label: 'basic', c: { video: true, audio: false } },
      { label: 'front-ideal', c: { video: { facingMode: 'user' }, audio: false } },
    ];
    let probeStream = null;
    let lastErr = null;
    let chosenLabel = null;
    for (const a of attempts) {
      try {
        log('probing constraints:', a.label, a.c);
        probeStream = await navigator.mediaDevices.getUserMedia(a.c);
        chosenLabel = a.label;
        log('probe success with:', a.label);
        break;
      } catch (e) {
        warn('probe failed:', a.label, e?.name, e?.message);
        lastErr = e;
      }
    }

    if (!probeStream) {
      // FALLBACK: prova direttamente Html5Qrcode.getCameras() che usa internamente meccanismi diversi
      warn('All getUserMedia probes failed, trying Html5Qrcode.getCameras() fallback');
      try {
        const cams = await Html5Qrcode.getCameras();
        log('Html5Qrcode.getCameras returned:', cams);
        if (cams && cams.length > 0) {
          const scanner = new Html5Qrcode('qr-reader');
          scannerRef.current = scanner;
          const camId = cams.find(c => /back|rear|environment/i.test(c.label))?.id || cams[0].id;
          log('Starting scanner with cam fallback:', camId);
          await scanner.start(camId, { fps: 10 },
            (decoded) => { handleScanSuccess(decoded); },
            () => {});
          setIsScanning(true);
          log('Scanner started via fallback');
          console.groupEnd();
          return;
        }
        toast.error('Nessuna fotocamera rilevata dal browser. Verifica permessi e che nessuna altra app la stia usando.');
      } catch (fbErr) {
        err('Fallback also failed:', fbErr?.name, fbErr?.message, fbErr);
        toast.error(mapErr(fbErr || lastErr));
      }
      console.groupEnd();
      return;
    }

    // Stop probe stream so html5-qrcode can take over
    try { probeStream.getTracks().forEach(t => t.stop()); } catch (e) {}
    log('probe stream stopped, handing off to html5-qrcode');

    // Now start html5-qrcode with constraint coherent with the probe success
    try {
      const scanner = new Html5Qrcode('qr-reader');
      scannerRef.current = scanner;
      const cameraConfig = chosenLabel === 'front-ideal'
        ? { facingMode: 'user' }
        : chosenLabel === 'basic'
        ? (videoDevices[0]?.deviceId ? { deviceId: { exact: videoDevices[0].deviceId } } : { facingMode: 'environment' })
        : { facingMode: 'environment' };
      log('html5-qrcode start with:', cameraConfig);
      await scanner.start(
        cameraConfig,
        { fps: 10 },
        (decodedText) => {
          handleManualScan(decodedText);
          scanner.stop().then(() => scanner.clear()).catch(() => {});
          scannerRef.current = null;
          setScanning(false);
        },
        () => {}
      );
      setScanning(true);
      log('scanner started OK');
      console.groupEnd();
    } catch (e) {
      err('html5-qrcode start failed:', e?.name, e?.message, e);
      toast.error(mapErr(e));
      console.groupEnd();
    }
  };


  const stopScanner = () => {
    if (scannerRef.current) {
      scannerRef.current.stop().then(() => {
        scannerRef.current.clear();
        scannerRef.current = null;
      }).catch(() => {});
    }
    setScanning(false);
  };

  
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const scanner = new Html5Qrcode('qr-reader-file');
      const result = await scanner.scanFile(file, true);
      scanner.clear();
      handleManualScan(result);
    } catch (err) {
      toast.error('QR code non trovato nell\'immagine');
    }
    e.target.value = '';
  };

  const handleManualScan = async (qrValue) => {
    if (!qrValue) return;
    try {
      const endpoint = gateType === 'mensa' ? `${API}/gate/public/mensa` : `${API}/gate/public/accesso`;
      const body = gateType === 'mensa'
        ? { gate_code: activeCode, access_pin: activeCode, qr_code: qrValue, tipo_pasto: tipoPasto }
        : { gate_code: activeCode, access_pin: activeCode, qr_code: qrValue, direzione };
      const res = await fetch(endpoint, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (res.status === 401 || res.status === 403) { try { sessionStorage.removeItem('campoAccessGranted'); sessionStorage.removeItem('campoAccessPin'); } catch(e){} toast.error('PIN scaduto. Reinserisci il codice.'); setStatus('input'); return; }
      const data = await res.json();
      if (data.ok) {
        setLastScan({ success: true, nome: data.nome, info: gateType === 'mensa' ? data.tipo_pasto : data.direzione });
        toast.success(`${data.nome} - ${gateType === 'mensa' ? data.tipo_pasto : data.direzione}`);
      } else if (data.error === 'duplicate') {
        setLastScan({ success: false, nome: data.nome, info: data.message });
        toast.warning(data.message);
      } else {
        toast.error(data.detail || data.message || 'Errore');
        setLastScan({ success: false, info: data.detail || 'Errore' });
      }
    } catch {
      toast.error('Errore di connessione');
    }
  };

  // Code input page
  if (status === 'input') {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-sm">
          <CardHeader className="text-center">
            <div className="mx-auto mb-2 bg-blue-100 rounded-full p-3 w-fit"><ShieldAlert className="w-8 h-8 text-blue-600" /></div>
            <CardTitle>Accesso Gate</CardTitle>
            <p className="text-sm text-muted-foreground">Inserisci il PIN di accesso a 6 cifre</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCodeSubmit} className="space-y-4">
              <Input value={inputCode} onChange={e => setInputCode(e.target.value.replace(/\D/g,'').slice(0,6))} placeholder="123456" inputMode="numeric" pattern="[0-9]*" maxLength={6} className="text-center text-2xl tracking-widest font-mono" autoFocus />
              <Button type="submit" className="w-full" disabled={inputCode.length !== 6}>Accedi</Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Loading
  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-white" />
      </div>
    );
  }

  // Invalid code
  if (status === 'invalid') {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-sm border-red-500">
          <CardHeader className="text-center">
            <div className="mx-auto mb-2 bg-red-100 rounded-full p-3 w-fit"><ShieldAlert className="w-8 h-8 text-red-600" /></div>
            <CardTitle className="text-red-600">Codice non valido</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-center mb-4">Codice non funzionante. Contattare l'amministratore in segreteria.</p>
            <Button onClick={() => { try { sessionStorage.removeItem('campoAccessGranted'); sessionStorage.removeItem('campoAccessPin'); } catch(e){} setInputCode(''); setStatus('input'); }} className="w-full">Riprova</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Valid - show gate interface
  return (
    <div className="min-h-screen bg-slate-900 text-white p-4">
      <div className="max-w-lg mx-auto">
        <div className="text-center mb-4">
          <h1 className="text-2xl font-bold">{gateLabel}</h1>
          <p className="text-slate-400 text-sm">Accesso esterno attivo</p>
        </div>

        {gateType === 'mobile' && (
          <div className="flex gap-2 mb-4">
            <Button onClick={() => setDirezione('ENTRATA')} variant={direzione === 'ENTRATA' ? 'default' : 'outline'} className={`flex-1 text-lg py-6 ${direzione === 'ENTRATA' ? 'bg-green-600 hover:bg-green-700' : ''}`}>
              <LogIn className="mr-2" /> ENTRATA
            </Button>
            <Button onClick={() => setDirezione('USCITA')} variant={direzione === 'USCITA' ? 'default' : 'outline'} className={`flex-1 text-lg py-6 ${direzione === 'USCITA' ? 'bg-red-600 hover:bg-red-700' : ''}`}>
              <LogOut className="mr-2" /> USCITA
            </Button>
          </div>
        )}

        {gateType === 'mensa' && (
          <div className="flex gap-2 mb-4">
            {['colazione','pranzo','cena'].map(p => (
              <Button key={p} onClick={() => setTipoPasto(p)} variant={tipoPasto === p ? 'default' : 'outline'} className={`flex-1 capitalize ${tipoPasto === p ? 'bg-orange-600 hover:bg-orange-700' : ''}`}>
                {p.toUpperCase()}
              </Button>
            ))}
          </div>
        )}

        <Card className="mb-4 bg-slate-800 border-slate-700">
          <CardContent className="p-4">
          <div id="qr-reader" className="w-full rounded-lg mb-2" style={{minHeight: scanning ? "300px" : "0px", display: scanning ? "block" : "none"}} />
          {scanning ? (
            <Button onClick={stopScanner} variant="outline" className="w-full">Ferma scanner</Button>
          ) : (
            <Button onClick={startScanner} className="w-full py-8 text-lg bg-blue-600 hover:bg-blue-700">
              <Camera className="mr-2 w-6 h-6" /> Avvia Scanner QR
            </Button>
          )}
          </CardContent>
        </Card>
            <Card className="mb-4 bg-slate-800 border-slate-700">
              <CardContent className="p-4 text-center">
                <div id="qr-reader-file" style={{display:"none"}} />
                <p className="text-sm text-slate-400 mb-2">Oppure carica un'immagine con QR code:</p>
                <input type="file" accept="image/*" ref={fileInputRef} onChange={handleFileUpload} style={{display:"none"}} />
                <Button onClick={() => fileInputRef.current?.click()} className="w-full py-3 text-lg bg-purple-600 hover:bg-purple-700">
                  Carica immagine QR
                </Button>
              </CardContent>
            </Card>

        <Card className="mb-4 bg-slate-800 border-slate-700">
          <CardContent className="p-4">
            <p className="text-sm text-slate-400 mb-2">Oppure inserisci il codice badge manualmente:</p>
            <div className="flex gap-2">
              <Input value={manualQr} onChange={e => setManualQr(e.target.value)} placeholder="Codice badge..." className="bg-slate-700 border-slate-600" />
              <Button onClick={() => { handleManualScan(manualQr); setManualQr(''); }} disabled={!manualQr}>Invia</Button>
            </div>
          </CardContent>
        </Card>

        {lastScan && (
          <Card className={`bg-slate-800 border-2 ${lastScan.success ? 'border-green-500' : 'border-red-500'}`}>
            <CardContent className="p-4 text-center">
              <p className="text-xl font-bold">{lastScan.nome || ''}</p>
              <p className={`text-lg ${lastScan.success ? 'text-green-400' : 'text-red-400'}`}>{lastScan.info}</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
